import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

interface UnifiedTagDisplayProps {
  tags: string[];
  userTags?: string[];
  maxRows?: number;
  maxTagsPerRow?: number;
  showAddButton?: boolean;
  onAddTag?: () => void;
  onDeleteTag?: (tag: string) => void;
  readOnly?: boolean;
  variant?: 'default' | 'compact' | 'minimal';
  sx?: any;
}

const UnifiedTagDisplay: React.FC<UnifiedTagDisplayProps> = ({
  tags = [],
  userTags = [],
  maxRows = 5,
  maxTagsPerRow = 8, // 增加每行標籤數量，適應更寬的欄位
  showAddButton = false,
  onAddTag,
  onDeleteTag,
  readOnly = false,
  variant = 'default',
  sx = {}
}) => {
  // 優先排序：自定義標籤在前，其他標籤在後
  const sortedTags = React.useMemo(() => {
    const customTags = tags.filter(tag => userTags.includes(tag));
    const otherTags = tags.filter(tag => !userTags.includes(tag));
    return [...customTags, ...otherTags];
  }, [tags, userTags]);

  // 限制顯示的標籤數量（基於最大行數和每行最大標籤數）
  const maxDisplayTags = maxRows * maxTagsPerRow;
  const displayTags = sortedTags.slice(0, maxDisplayTags);

  // 將標籤分組為行，基於寬度動態換行，確保最多顯示指定行數
  const tagRows = React.useMemo(() => {
    const rows = [];
    let currentRow = [];
    let currentRowWidth = 0;
    const maxRowWidth = 500; // 對應 Tag 欄位的最大寬度
    
    for (let i = 0; i < displayTags.length && rows.length < maxRows; i++) {
      const tag = displayTags[i];
      // 估算標籤寬度（基於文字長度和字體大小，考慮中文字符）
      const chineseCharCount = (tag.match(/[\u4e00-\u9fff]/g) || []).length;
      const englishCharCount = tag.length - chineseCharCount;
      const estimatedWidth = chineseCharCount * (variant === 'compact' ? 12 : variant === 'minimal' ? 10 : 14) + 
                            englishCharCount * (variant === 'compact' ? 6 : variant === 'minimal' ? 5 : 7) + 
                            40; // 40px 為 padding 和間距
      
      // 如果加入這個標籤會超出寬度，且當前行不為空，則換行
      if (currentRowWidth + estimatedWidth > maxRowWidth && currentRow.length > 0) {
        rows.push([...currentRow]);
        // 檢查是否已達到最大行數
        if (rows.length >= maxRows) {
          break;
        }
        currentRow = [tag];
        currentRowWidth = estimatedWidth;
      } else {
        currentRow.push(tag);
        currentRowWidth += estimatedWidth;
      }
    }
    
    // 添加最後一行（如果未超過最大行數）
    if (currentRow.length > 0 && rows.length < maxRows) {
      rows.push(currentRow);
    }
    
    return rows;
  }, [displayTags, maxRows, variant]);

  // 計算實際顯示的標籤總數
  const actualDisplayCount = tagRows.reduce((total, row) => total + row.length, 0);
  
  // 檢查是否還有更多標籤未顯示
  const remainingCount = sortedTags.length - actualDisplayCount;
  const hasMoreTags = remainingCount > 0;

  const getChipStyle = (tag: string) => {
    const isCustomTag = userTags.includes(tag);
    
    switch (variant) {
      case 'compact':
        return {
          fontSize: '0.75rem',
          height: '24px',
          backgroundColor: isCustomTag ? '#e3f2fd' : '#f5f5f5',
          color: isCustomTag ? '#1976d2' : '#666',
          '&:hover': {
            backgroundColor: isCustomTag ? '#bbdefb' : '#e0e0e0'
          }
        };
      case 'minimal':
        return {
          fontSize: '0.65rem',
          height: '20px',
          backgroundColor: isCustomTag ? '#e8f5e8' : '#f0f0f0',
          color: isCustomTag ? '#2e7d32' : '#666',
          '&:hover': {
            backgroundColor: isCustomTag ? '#c8e6c9' : '#e0e0e0'
          }
        };
      default:
        return {
          fontSize: '0.8rem',
          height: '26px',
          backgroundColor: isCustomTag ? '#e3f2fd' : '#f5f5f5',
          color: isCustomTag ? '#1976d2' : '#666',
          '&:hover': {
            backgroundColor: isCustomTag ? '#bbdefb' : '#e0e0e0'
          }
        };
    }
  };

  if (tags.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.5,
        minHeight: variant === 'compact' ? '140px' : '160px',
        ...sx 
      }}>
        {/* 顯示「+ 新增標籤」按鈕 - 即使沒有標籤也顯示 */}
        {showAddButton && onAddTag && !readOnly && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            minHeight: variant === 'compact' ? '24px' : '28px'
          }}>
            <Chip
              label="+ 新增標籤"
              size="small"
              variant="outlined"
              onClick={onAddTag}
              sx={{
                fontSize: '0.75rem',
                height: '26px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 0.5, // 增加行間距
      minHeight: variant === 'compact' ? '140px' : '160px', // 增加最小高度以容納更多內容
      ...sx 
    }}>
      {/* 顯示標籤行 */}
      {tagRows.map((rowTags, rowIndex) => (
        <Box key={rowIndex} sx={{ 
          display: 'flex', 
          gap: 0.5, // 增加標籤間距
          minHeight: variant === 'compact' ? '24px' : '28px', // 增加每行最小高度
          alignItems: 'center',
          mb: 0.2, // 增加行間距
          overflow: 'hidden' // 防止標籤溢出
        }}>
          {rowTags.map((tag, tagIndex) => (
            <Chip
              key={`${tag}-${rowIndex}-${tagIndex}`}
              label={tag}
              size="small"
              onDelete={!readOnly && onDeleteTag ? () => onDeleteTag(tag) : undefined}
              sx={getChipStyle(tag)}
            />
          ))}
          {/* 在最後一行的最後一個標籤後方顯示 "more" 提示 */}
          {rowIndex === tagRows.length - 1 && hasMoreTags && (
            <Chip
              label={`+${remainingCount} more`}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.7rem',
                height: variant === 'compact' ? '24px' : '26px',
                backgroundColor: '#f0f0f0',
                color: '#666',
                borderColor: '#ddd',
                '&:hover': {
                  backgroundColor: '#e0e0e0'
                }
              }}
            />
          )}
        </Box>
      ))}
      
      {/* 顯示「+ 新增標籤」按鈕 - 無論是否有標籤都顯示 */}
      {showAddButton && onAddTag && !readOnly && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mt: 0.5, // 增加與上方內容的間距
          minHeight: variant === 'compact' ? '24px' : '28px' // 增加按鈕區域高度
        }}>
          <Chip
            label="+ 新增標籤"
            size="small"
            variant="outlined"
            onClick={onAddTag}
            sx={{
              fontSize: '0.7rem',
              height: '24px', // 增加按鈕高度
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#f0f0f0'
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default UnifiedTagDisplay;
